import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { MANUAL_STATUSES, STATUS_LABELS, type ManualStatus } from '@homework/shared';
import { useRealtime } from '../lib/realtime';
import { useToast } from '../lib/toast';
import { useMe } from '../lib/auth';
import { STATUS_COLORS, StatusDot, cx } from './ui';

const HELP: Record<ManualStatus, string> = {
  AVAILABLE: 'Teammates can walk over and talk to you.',
  FOCUS: 'People have to knock and wait for you to accept.',
  BREAK: 'Your timer pauses and break time is tracked separately.',
  AWAY: 'Your timer pauses until you come back.',
};

/** FR-3.2 — manual status switching, with focus mode explained in place. */
export function StatusSwitcher() {
  const me = useMe();
  const { presence, timer, setStatus } = useRealtime();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<ManualStatus | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const current = timer?.status ?? presence.get(me.user.id)?.status ?? 'OFFLINE';

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = async (status: ManualStatus) => {
    setPending(status);
    try {
      await setStatus(status);
      setOpen(false);
    } catch (error) {
      push({ kind: 'error', title: 'Status not changed', body: (error as Error).message });
    } finally {
      setPending(null);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={cx('btn-secondary', 'min-w-[9.5rem] justify-between')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="flex items-center gap-2">
          <StatusDot status={current} />
          <span className={STATUS_COLORS[current].text}>{STATUS_LABELS[current]}</span>
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
      </button>

      {open ? (
        <div
          role="menu"
          className="glass-strong absolute right-0 z-30 mt-2 w-72 animate-slide-up rounded-3xl p-2"
        >
          {MANUAL_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              role="menuitem"
              disabled={pending !== null}
              className="flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-white/70 disabled:opacity-60"
              onClick={() => void choose(status)}
            >
              <StatusDot status={status} className="mt-1.5" />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  {STATUS_LABELS[status]}
                  {current === status ? <Check className="h-3.5 w-3.5 text-brand-600" aria-hidden /> : null}
                </span>
                <span className="mt-0.5 block text-xs text-slate-500">{HELP[status]}</span>
              </span>
            </button>
          ))}
          {current === 'MEETING' ? (
            <p className="mt-1 border-t border-white/60 px-3 py-2 text-xs text-slate-500">
              You are in a meeting — from your calendar, or because you are standing in a meeting room. Your own
              choice comes back when you leave it.
            </p>
          ) : null}
          {current === 'BREAK' ? (
            <p className="mt-1 border-t border-white/60 px-3 py-2 text-xs text-slate-500">
              You are in the breakroom. Walk out and you go back to the status you chose.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
